import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthResponse, User } from '../types';
import { authService } from '../services/authService';
import InactivityWarning from '../components/InactivityWarning';
import { applyCompanyBrand, clearCompanyBrand } from '../utils/brand';

interface AuthContextType {
  user: User | null;
  login: (identifier: string, password: string) => Promise<AuthResponse>;
  logout: () => void;
  register: (data: any) => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  refreshUser: () => Promise<void>;
  completeTwoFactorLogin: (twoFactorTempToken: string, code: string) => Promise<AuthResponse>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  useEffect(() => {
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const userData = authService.getCurrentUser();
          setUser(userData);
          try {
            const fresh = await authService.getMe();
            if (fresh.user) {
              setUser(fresh.user);
              authService.updateStoredUser(fresh.user);
            }
          } catch (refreshError) {
            console.error('Error refreshing user branding:', refreshError);
          }
        } catch (error) {
          console.error('Error loading user:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (user?.company) {
      applyCompanyBrand(user.company);
      return;
    }
    clearCompanyBrand();
  }, [user]);

  // Activity tracking i auto logout nakon 15 minuta neaktivnosti
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minuta u milisekundama
    const WARNING_TIMEOUT = 14 * 60 * 1000; // 14 minuta - prikaži upozorenje 1 minut pre

    // Funkcija koja proverava neaktivnost
    const checkInactivity = () => {
      const lastActivity = authService.getLastActivity();
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      // Automatski logout nakon 15 minuta
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        console.log('🔒 Auto logout - 15 minutes of inactivity');
        setShowInactivityWarning(false);
        logout();
        return;
      }

      // Prikaži upozorenje 1 minut pre logout-a
      if (timeSinceLastActivity >= WARNING_TIMEOUT && !showInactivityWarning) {
        setShowInactivityWarning(true);
        const remaining = Math.floor((INACTIVITY_TIMEOUT - timeSinceLastActivity) / 1000);
        setSecondsRemaining(remaining);
      }

      // Ažuriraj preostale sekunde ako je upozorenje aktivno
      if (showInactivityWarning) {
        const remaining = Math.floor((INACTIVITY_TIMEOUT - timeSinceLastActivity) / 1000);
        setSecondsRemaining(Math.max(0, remaining));
      }
    };

    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      authService.updateActivity();
      // Sakrij upozorenje ako je bilo prikazano
      if (showInactivityWarning) {
        setShowInactivityWarning(false);
      }
    };

    // Dodaj event listenere
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Proveri neaktivnost svake sekunde (za precizno odbrojavanje)
    const inactivityCheck = setInterval(checkInactivity, 1000);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(inactivityCheck);
    };
  }, [user]);

  const login = async (identifier: string, password: string) => {
    const response = await authService.login(identifier, password);
    if (response.user) {
      setUser(response.user);
    }
    return response;
  };

  const completeTwoFactorLogin = async (twoFactorTempToken: string, code: string) => {
    const response = await authService.verifyTwoFactorLogin(twoFactorTempToken, code);
    if (response.user) {
      setUser(response.user);
    }
    return response;
  };

  const register = async (data: any) => {
    const response = await authService.register(data);
    if (response.user) {
      setUser(response.user);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((current) => {
      if (!current) {
        return current;
      }

      const updatedUser = { ...current, ...updates };
      authService.updateStoredUser(updatedUser);
      return updatedUser;
    });
  };

  const refreshUser = async () => {
    const response = await authService.getMe();
    if (response.user) {
      setUser(response.user);
      authService.updateStoredUser(response.user);
    }
  };

  const handleStayActive = () => {
    authService.updateActivity();
    setShowInactivityWarning(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        register,
        updateUser,
        refreshUser,
        completeTwoFactorLogin,
        isAuthenticated: !!user,
        loading
      }}
    >
      {children}
      <InactivityWarning
        visible={showInactivityWarning}
        secondsRemaining={secondsRemaining}
        onStayActive={handleStayActive}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}; 